import type { DocumentViewer, DocumentViewerConfig } from '@/interfaces/document-viewer'
import { PDFDocumentViewer } from '@/components/pdf-document-viewer'
import { ImageViewer } from '@/components/image-viewer'
import { logger } from '@/utils/logger'

/**
 * Factory for creating document viewers based on document type
 * Implements the factory pattern for proper document type routing
 */
export class DocumentViewerFactory {
  /**
   * Create a document viewer based on document type
   */
  static createViewer(documentType: string, config: DocumentViewerConfig): DocumentViewer {
    logger.debug(`Creating document viewer for type: ${documentType}`)
    
    switch (documentType.toLowerCase()) {
      case 'pdf':
        return new PDFDocumentViewer(
          config.container,
          config.apiService,
          config.appState
        )
        
      case 'image':
        return new ImageViewer(
          config.container,
          config.apiService
        )
        
      case 'manual':
        // TODO: Implement ManualEntryViewer
        throw new Error('ManualEntryViewer not yet implemented. Use existing manual entry handling.')
        
      default:
        throw new Error(`Unsupported document type: ${documentType}. Supported types: pdf, image, manual`)
    }
  }
  
  /**
   * Check if a document type is supported
   */
  static isSupported(documentType: string): boolean {
    const supportedTypes = ['pdf', 'image', 'manual']
    return supportedTypes.includes(documentType.toLowerCase())
  }
  
  /**
   * Get list of supported document types
   */
  static getSupportedTypes(): string[] {
    return ['pdf', 'image', 'manual']
  }
  
  /**
   * Create viewer with error handling
   */
  static createViewerSafe(documentType: string, config: DocumentViewerConfig): DocumentViewer | null {
    try {
      return this.createViewer(documentType, config)
    } catch (error) {
      logger.error(`Failed to create viewer for type ${documentType}:`, error)
      return null
    }
  }
  
  /**
   * Get default viewer type for a file extension
   */
  static getViewerTypeForExtension(filename: string): string {
    const extension = filename.toLowerCase().split('.').pop()
    
    switch (extension) {
      case 'pdf':
        return 'pdf'
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'tiff':
      case 'webp':
        return 'image'
      default:
        return 'pdf' // Default fallback
    }
  }
  
  /**
   * Get viewer type from document metadata
   */
  static getViewerTypeFromDocument(document: any): string {
    logger.debug('🔍 Document type detection:', {
      document_type: document.document_type,
      content_type: document.content_type,
      filename: document.filename,
      original_filename: document.original_filename
    })
    
    // Check document_type field first
    if (document.document_type) {
      const detectedType = document.document_type.toLowerCase()
      logger.debug('✅ Using document_type field:', detectedType)
      return detectedType
    }
    
    // Check content_type
    if (document.content_type) {
      const contentType = document.content_type.toLowerCase()
      if (contentType.includes('pdf')) {
        logger.debug('✅ Detected PDF from content_type:', contentType)
        return 'pdf'
      }
      if (contentType.includes('image')) {
        logger.debug('✅ Detected image from content_type:', contentType)
        return 'image'
      }
    }
    
    // Check filename extension
    if (document.filename || document.original_filename) {
      const filename = document.filename || document.original_filename
      const detectedType = this.getViewerTypeForExtension(filename)
      logger.debug('✅ Detected type from filename:', { filename, detectedType })
      return detectedType
    }
    
    // Default fallback - try to be smarter about this
    logger.warn('⚠️ No document type detected, using PDF as fallback')
    return 'pdf'
  }
}
